import _ from 'lodash';
import { action, observable, makeObservable } from 'mobx';
import { AssignCarrierServiceAndWarehouseForm } from 'modules/order/forms';
import { BaseViewStore } from 'core/stores';
import {
	notificationError,
	isSuperAdminOrganization,
	getDistinctProductIds,
	isSome,
	mapCarrierServiceWithRates,
	translate,
	shouldUseBuyShipping,
	getBulkItemsSellerId,
} from 'core/utils';
import { ProductInventoryStore } from 'modules/product-inventory/stores';
import { FulfillmentOrderStore } from 'modules/order/stores';
import { CarrierServiceStore } from 'modules/carrier/stores';
import { RateServiceStore } from 'modules/rate/stores';
import { ApplicationStore } from 'platform/application/stores';
import { channelType, onAssignStatuses, sourceOfChange } from 'common/constants';

class AssignCarrierServiceAndWarehouseViewStore extends BaseViewStore {
	@observable useBuyShipping = false;
	@observable ignoreDeliverByDate = true;
	@observable carrierServiceOptions = [];
	@observable tempCarrierServiceOptions = [];
	@observable pageCarrierService = 1;
	@observable warehouseOptions = [];
	@observable page = 1;
	@observable responseArray = [];
	@observable isCarrierServiceLoading = false;
	@observable searchPhrase = null;
	@observable isWarehouseDropdownLoading = false;
	selectedItem = null;
	selectedItems = null;
	isBulkAction = false;
	sellerId = null;
	isAllAmazon = false;
	debouncedOnCheck = null;
	debouncedWarehouseSearch = null;
	debouncedCarrierServiceSearch = null;

	form = new AssignCarrierServiceAndWarehouseForm({
		onSuccess: async () => {
			if (this.selectedItems != null) {
				await this.assignCarrierServiceAndWarehouseBulk();
			} else {
				await this.assignCarrierServiceAndWarehouse();
			}
		},
	});

	constructor(rootStore, { selectedItemId, isDetailPage, parentViewStore, selectedItemIds }) {
		super(rootStore);
		makeObservable(this);
		this.isDetailPage = isDetailPage;
		this.parentView = parentViewStore;
		this.productInventoryStore = new ProductInventoryStore(rootStore);
		this.carrierServiceStore = new CarrierServiceStore(rootStore);
		this.rateServiceStore = new RateServiceStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.debouncedWarehouseSearch = _.debounce((searchPhrase, { action }) => {
			this.searchWarehouseAsync(searchPhrase, { action });
		}, 500);
		this.debouncedCarrierServiceSearch = _.debounce((searchPhrase, { action }) => {
			this.searchCarrierServiceAsync(searchPhrase, { action });
		}, 500);
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		this.initialize(selectedItemId, selectedItemIds);
	}

	@action.bound
	initialize = async (selectedItemId, selectedItemIds) => {
		this.suspend();
		if (selectedItemIds != null) {
			await this.findFulfillmentOrders(selectedItemIds);
			this.isBulkAction = true;
			this.isAllAmazon =
				_.find(this.selectedItems, item => item.order.channel.channelType.name !== channelType.amazon) === undefined
					? true
					: false;
			this.sellerId = getBulkItemsSellerId(this.selectedItems);
			this.useBuyShipping = false;
			this.ignoreDeliverByDate = this.isAllAmazon ? !this.useBuyShipping : true;
			this.form.$('ignoreDeliverByDate').value = this.ignoreDeliverByDate;
		} else {
			await this.getFulfillmentOrder(selectedItemId);
			this.useBuyShipping = shouldUseBuyShipping(this.selectedItem, this.form);
			this.isVendorOrder = this.selectedItem.order.channel.channelType.name === channelType.amazonVendor;
			this.ignoreDeliverByDate =
				this.selectedItem.order.channel.channelType.name === channelType.amazon ? !this.useBuyShipping : true;
			this.form.$('ignoreDeliverByDate').value = this.ignoreDeliverByDate;
		}
		await this.findWarehouses();
		await this.findCarrierServices(this.pageCarrierService);
		this.resume();
	};

	@action.bound
	findFulfillmentOrders = async selectedItemIds => {
		this.selectedItems = (await this.fulfillmentOrderStore.findFulfillmentOrder({
			page: 1,
			rpp: 100,
			ids: selectedItemIds,
			embed: [
				'fulfillmentOrderStatus',
				'fulfillmentOrderItems',
				'fulfillmentOrderItems.product',
				'order',
				'order.channel',
				'order.channel.channelType',
			],
		})).item;
	};

	@action.bound
	getFulfillmentOrder = async selectedItemId => {
		this.selectedItem = await this.fulfillmentOrderStore.getFulfillmentOrder(selectedItemId, {
			embed: [
				'fulfillmentOrderItems',
				'fulfillmentOrderItems.product',
				'fulfillmentOrderItems.orderLine',
				'order',
				'order.shippingCoreCountry',
				'order.shippingCoreCountryState',
				'order.channel',
				'order.channel.channelType',
			],
		});
	};

	@action.bound
	findCarrierServices = async (page, shouldSuspend = true, searchPhrase = null) => {
		if (shouldSuspend) {
			this.suspend();
		}
		this.isCarrierServiceLoading = true;
		let options = [];
		// Buy Shipping.
		if (
			this.useBuyShipping &&
			!this.isBulkAction &&
			this.form.$('assignedWarehouse').value !== '' &&
			this.selectedItem.order.channel.channelType.name === channelType.amazon
		) {
			await this.rateServiceStore.getAmazonShippingServices({
				fulfillmentOrderId: this.selectedItem.id,
				warehouseId: this.form.$('assignedWarehouse').value.value,
			});
			options = options = mapCarrierServiceWithRates(this.rateServiceStore.response.carrierServices, true);
		}
		// Bulk Action.
		else if (this.isBulkAction || this.isVendorOrder) {
			await this.carrierServiceStore.findCarrierService({
				page: 1,
				rpp: 50,
				embed: ['carrier'],
				requiredCarrierService: this.isVendorOrder ? this.selectedItem.order.requiredCarrierService : null,
			});
			options = _.map(this.carrierServiceStore.data, item => {
				return { value: item.id, label: item.name, extra: item };
			});
		}
		// Non Buy Shipping.
		else {
			if (this.form.$('assignedWarehouse').value !== '') {
				await this.rateServiceStore.findCarrierServiceWithRates({
					rateSearchRest: { search: searchPhrase, rpp: 30, page: page },
					fulfillmentOrderId: this.selectedItem.id,
					warehouseId: this.form.$('assignedWarehouse').value.value,
					channelId: this.selectedItem.order.channelId,
					isReturn: false,
					ignoreDeliverByDate: this.ignoreDeliverByDate,
				});
				options = mapCarrierServiceWithRates(this.rateServiceStore.data);
				options = _.sortBy(options, ['label']);
			}
		}
		this.tempCarrierServiceOptions = [...options];
		this.carrierServiceOptions = this.tempCarrierServiceOptions;
		this.isCarrierServiceLoading = false;
		if (shouldSuspend) {
			this.resume();
		}
	};

	@action.bound
	async searchCarrierServiceAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageCarrierService = 1;
		}
		if (action === 'input-change') {
			let newOptions = [];
			this.searchPhrase = searchPhrase;
			this.isCarrierServiceLoading = true;
			if (!this.isBulkAction && !this.isVendorOrder) {
				await this.rateServiceStore.findCarrierServiceWithRates({
					rateSearchRest: { searchQuery: searchPhrase || null, rpp: 30, page: 1 },
					fulfillmentOrderId: this.selectedItem.id,
					warehouseId: this.form.$('assignedWarehouse').value.value,
					channelId: this.selectedItem.order.channelId,
					ignoreDeliverByDate: this.ignoreDeliverByDate,
				});
				newOptions = mapCarrierServiceWithRates(this.rateServiceStore.data);
			} else {
				await this.carrierServiceStore.findCarrierService({
					rpp: 30,
					page: 1,
					searchQuery: searchPhrase,
					requiredCarrierService: this.isVendorOrder ? this.selectedItem.order.requiredCarrierService : null,
				});
				newOptions = _.map(this.carrierServiceStore.data, item => {
					return {
						value: item.id,
						label: item.name,
						extra: item,
					};
				});
			}
			newOptions = _.sortBy(newOptions, ['label']);
			this.carrierServiceOptions = newOptions;
			this.isCarrierServiceLoading = false;
		}
	}

	@action.bound
	async handleOnServiceCarrierMenuScroll() {
		if (this.carrierServiceOptions.length < this.pageCarrierService * 15) return;
		this.isCarrierServiceLoading = true;
		this.pageCarrierService++;
		await this.findCarrierServices(this.pageCarrierService, false, this.searchPhrase);
		if (this.tempCarrierServiceOptions.length > this.carrierServiceOptions.length) {
			this.carrierServiceOptions = this.tempCarrierServiceOptions;
		}
	}

	composeWarehouseFilter(page, search) {
		let filter = {
			rpp: 15,
			page: page,
			warehouseOnly: true,
			isOrganizationActive: true,
		};
		if (this.sellerId) {
			filter.includeOnlySellerWarehouses = true;
			filter.sellerWarehousesOnlyOrganizationId = this.sellerId;
		}
		if (search) {
			filter.search = search;
		}
		return filter;
	}

	@action.bound
	async handleOnWarehouseMenuScroll() {
		if (this.warehouseOptions.length < this.page * 15) return;
		this.isWarehouseDropdownLoading = true;
		this.page++;
		let newOptions = [];
		if (!this.isBulkAction) {
			const productIds = getDistinctProductIds(this.selectedItem.fulfillmentOrderItems);
			const response = await this.productInventoryStore.findProductInventory({
				rpp: 15,
				page: this.page,
				search: this.searchPhrase,
				searchFields: 'assignedWarehouse.applicationTitle',
				productIds: productIds,
				isOrganizationActive: true,
				useSafeQuantity: false,
				calculateInventory: !this.isBulkAction,
				filterInapplicableWarehouses: !this.isBulkAction,
				includeOnlySellerWarehouses: true,
				sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
				embed: ['assignedWarehouse'],
				requiredWarehouse: this.isVendorOrder ? this.selectedItem.order.requiredWarehouse : null,
			});
			newOptions = _.map(response.item, productInventory => {
				return {
					value: productInventory.assignedWarehouse.id,
					label: productInventory.assignedWarehouse.applicationTitle,
				};
			});
		} else {
			let filter = this.composeWarehouseFilter(1);
			const response = await this.applicationStore.find(filter);
			newOptions = _map(response.item, warehouse => {
				return {
					value: warehouse.id,
					label: warehouse.applicationTitle,
				};
			});
		}
		if (newOptions.length > this.warehouseOptions.length) {
			this.warehouseOptions = [...newOptions];
		}
		this.isWarehouseDropdownLoading = false;
	}

	@action.bound
	async onMenuClose(item) {
		this.selectedWarehouse = item;
		this.page = 1;
		this.searchPhrase = null;
		await this.findWarehouses();
	}

	@action.bound
	async searchWarehouseAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.page = 1;
		}
		if (action === 'input-change') {
			this.isWarehouseDropdownLoading = true;
			this.searchPhrase = searchPhrase;
			this.page = 1;
			this.warehouseOptions = [];
			const newOptions = [];
			if (!this.isBulkAction) {
				const productIds = getDistinctProductIds(this.selectedItem.fulfillmentOrderItems);
				const response = await this.productInventoryStore.findProductInventory({
					rpp: 15,
					page: this.page,
					search: this.searchPhrase,
					searchFields: 'assignedWarehouse.applicationTitle',
					fulfillmentOrderId: this.selectedItem.id,
					productIds: productIds,
					isOrganizationActive: true,
					useSafeQuantity: false,
					calculateInventory: !this.isBulkAction,
					filterInapplicableWarehouses: !this.isBulkAction,
					includeOnlySellerWarehouses: true,
					sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
					embed: ['assignedWarehouse'],
					requiredWarehouse: this.isVendorOrder ? this.selectedItem.order.requiredWarehouse : null,
				});
				_.forEach(response.item, productInventory => {
					newOptions.push({
						value: productInventory.assignedWarehouse.id,
						label: productInventory.assignedWarehouse.applicationTitle,
					});
				});
			} else {
				let filter = this.composeWarehouseFilter(this.page, this.searchPhrase);
				const response = await this.applicationStore.find(filter);
				_.forEach(response.item, warehouse => {
					newOptions.push({
						value: warehouse.id,
						label: warehouse.applicationTitle,
					});
				});
			}
			this.warehouseOptions = newOptions;
			this.isWarehouseDropdownLoading = false;
		}
	}

	@action.bound
	findWarehouses = async () => {
		if (!this.isBulkAction) {
			const response = await this.productInventoryStore.findProductInventory({
				rpp: 15,
				page: this.page,
				search: this.searchPhrase,
				searchFields: 'assignedWarehouse.applicationTitle',
				fulfillmentOrderId: this.selectedItem.id,
				productIds: getDistinctProductIds(this.selectedItem.fulfillmentOrderItems),
				isOrganizationActive: true,
				useSafeQuantity: false,
				calculateInventory: !this.isBulkAction,
				filterInapplicableWarehouses: !this.isBulkAction,
				includeOnlySellerWarehouses: true,
				sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
				embed: ['assignedWarehouse'],
				requiredWarehouse: this.isVendorOrder ? this.selectedItem.order.requiredWarehouse : null,
			});
			this.warehouseOptions = _.map(response.item, productInventory => {
				return {
					label: productInventory.assignedWarehouse.applicationTitle,
					value: productInventory.assignedWarehouse.id,
				};
			});
		} else {
			let filter = this.composeWarehouseFilter(this.page, this.searchPhrase);
			const response = await this.applicationStore.find(filter);
			this.warehouseOptions = _.map(response.item, warehouse => {
				return {
					label: warehouse.applicationTitle,
					value: warehouse.id,
				};
			});
		}
	};

	onBack = () => {
		this.parentView.assignmentModal.close();
	};

	onCheck = async check => {
		if (check) {
			this.form.$('useBuyShipping').value = check.currentTarget.checked;
			this.useBuyShipping = check.currentTarget.checked;
			this.form.$('assignedCarrierService').value = null;
			if (this.form.$('assignedWarehouse').value !== '' && !this.isBulkAction) {
				this.suspend();
				await this.findCarrierServices(this.pageCarrierService, false);
				this.resume();
			}
		}
	};

	@action.bound
	onCarrierServiceSelect = carrierService => {
		if (carrierService && carrierService.value) {
			this.form.$('assignedCarrierService').value = carrierService;
		} else {
			this.form.$('assignedCarrierService').value = '';
		}
	};

	@action.bound
	onWarehouseSelect = async warehouse => {
		if (warehouse && warehouse.value) {
			this.form.$('assignedWarehouse').value = warehouse;
			this.selectedWarehouse = warehouse;
			this.pageCarrierService = 1;
			this.tempCarrierServiceOptions = [];
			if (this.useBuyShipping) {
				this.form.$('assignedCarrierService').value = null;
			}
			if (!this.isBulkAction) {
				this.suspend();
				await this.findCarrierServices(this.pageCarrierService);
				this.resume();
			}
		} else {
			this.form.$('assignedWarehouse').value = '';
			this.pageCarrierService = 1;
			this.tempCarrierServiceOptions = [];
			this.carrierServiceOptions = [];
			if (this.useBuyShipping) {
				this.form.$('assignedCarrierService').value = null;
			}
		}
	};

	@action.bound
	assignCarrierServiceAndWarehouse = async () => {
		const { assignedWarehouse, assignedCarrierService, useBuyShipping } = this.form.values();
		const data = {
			assignedWarehouseId: assignedWarehouse.value,
			assignedCarrierServiceId: this.isVendorOrder
				? assignedCarrierService.value
				: assignedCarrierService.value.carrierServiceId,
			assignedCarrierId: assignedCarrierService.value.carrierId != null ? assignedCarrierService.value.carrierId : null,
			assignedRate:
				assignedCarrierService.extra.carrier != null &&
				(assignedCarrierService.extra.carrier.isThirdParty || assignedCarrierService.extra.carrier.isPartialThirdParty)
					? 0
					: assignedCarrierService.extra.rate,
			assignedRateSurcharge:
				assignedCarrierService.extra.carrier != null
					? isSome(assignedCarrierService.extra.carrier.surplusCharge)
						? assignedCarrierService.extra.carrier.isThirdParty ||
						  assignedCarrierService.extra.carrier.isPartialThirdParty
							? 0 // carrier has surcharge but it is third party, so it's saved as 0
							: assignedCarrierService.extra.rateComposition.surcharge // carrier has surcharge and its not third party
						: null // carrier has no surcharge
					: null,
			useBuyShipping: useBuyShipping,
			fulfillmentOrderId: this.selectedItem.id,
		};
		if (this.selectedItem.isChecked) {
			delete this.selectedItem.isChecked;
		}
		try {
			await this.fulfillmentOrderStore.assignCarrierServiceAndWarehouse(data, sourceOfChange.assignCarrierAndWarehouse);
			this.rootStore.notificationStore.success(['FULFILLMENTORDER.ASSIGNMODAL.SUCCESS']);
		} catch (err) {
			if (err.statusCode == 400 && err.data == 'FULFILLMENTORDER.ALREADYPROCESSING') {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.ALREADYPROCESSING',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			} else {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.ASSIGNMODAL.FAIL',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			}
		}
		if (this.isDetailPage) {
			this.parentView.initialize();
		} else {
			this.parentView.findFulfillmentOrders();
		}
		this.parentView.assignmentModal.close();
	};

	@action.bound
	assignCarrierServiceAndWarehouseBulk = async () => {
		const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.ASSIGN'));
		try {
			const { assignedWarehouse, assignedCarrierService, ignoreDeliverByDate } = this.form.values();
			const selectedItemsIds = [];
			const notProcessableItems = [];

			this.selectedItems.map(selectedItem => {
				let index = _.findIndex(onAssignStatuses, item => item == selectedItem.fulfillmentOrderStatus.abrv);
				if (index != -1) {
					if (assignedCarrierService != null) {
						selectedItem.assignedCarrierServiceId = assignedCarrierService.value;
						selectedItem.carrierService = assignedCarrierService.extra;
					}
					selectedItem.assignedWarehouseId = assignedWarehouse.value;
					selectedItem.warehouseOrganization = assignedWarehouse.extra;
					selectedItemsIds.push(selectedItem.id);
				} else {
					notProcessableItems.push(selectedItem);
				}
			});
			if (selectedItemsIds.length > 0) {
				this.parentView.reload = true;
				this.parentView.assignModalBulk.close();
				//this.resume();
				this.parentView.selectedItems.forEach(o => delete o.isChecked);
				this.parentView.isSelectAllCheckboxSelected = false;
				this.parentView.selectedItems = [];
				if (this.parentView.selectedGroupByOption) {
					this.parentView.selectedGroups.forEach(g => delete g.isChecked);
					this.parentView.selectedGroups = [];
				}
				const data = {
					assignedWarehouseId: assignedWarehouse.value,
					assignedCarrierServiceId: assignedCarrierService.value,
					assignedRate: null,
					fulfillmentOrderIds: selectedItemsIds,
					useBuyShipping: this.useBuyShipping,
					ignoreDeliverByDate: this.isSuperAdminOrganization ? ignoreDeliverByDate : true,
				};
				const count = await this.fulfillmentOrderStore.assignCarrierServiceAndWarehouseBulk(
					data,
					sourceOfChange.assignCarrierAndWarehouseBulk
				);
				if (count > 0) {
					if (count == selectedItemsIds.length) {
						this.rootStore.notificationStore.success([
							'FULFILLMENTORDER.ASSIGNMODAL.SUCCESSBULK',
							{
								count: count,
								countMax: selectedItemsIds.length,
								notProcessable: notProcessableItems.length,
							},
						]);
					} else {
						this.rootStore.notificationStore.success([
							'FULFILLMENTORDER.ASSIGNMODAL.PARTIALSUCCESSBULK',
							{
								count: count,
								countMax: selectedItemsIds.length,
								notProcessable: notProcessableItems.length,
							},
						]);
					}
					this.parentView.findFulfillmentOrders();
				} else {
					this.rootStore.notificationStore.error('ORDERVIEW.NOVALIDORDERSELECTEDFORASSIGN');
				}
			} else {
				this.rootStore.notificationStore.error('ORDERVIEW.NOVALIDORDERSELECTEDFORASSIGN');
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.rootStore.notificationStore.dismiss(notificationId);
	};

	@action.bound
	onIgnoreDeliverByDateChange = async value => {
		this.ignoreDeliverByDate = value;
		if (!this.isBulkAction) {
			if (this.selectedItem.order.channel.channelType.name === channelType.amazon) {
				this.form.$('useBuyShipping').value = !this.ignoreDeliverByDate;
				this.useBuyShipping = !this.ignoreDeliverByDate;
			}

			if (this.form.$('assignedWarehouse').value) {
				this.form.$('assignedCarrierService').value = null;
				await this.findCarrierServices(this.pageCarrierService);
			}
		}

		if (this.isBulkAction) {
			this.form.$('useBuyShipping').value = !this.ignoreDeliverByDate;
			this.useBuyShipping = !this.ignoreDeliverByDate;
		}
	};
}

export default AssignCarrierServiceAndWarehouseViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/AssignCarrierServiceAndWarehouseViewStore.js